import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import MaturityRadar from '../components/common/MaturityRadar'
import './maturity-analysis-pdf.css'
import { formatDecimal } from '../utils/formatNumbers'
import brandLogo from '../views/layouts/images/logo1-close.png'

export type MaturityHbarRow = {
  code: string
  name: string
  v: number
}

export type MaturityPdfProps = {
  computedOn: string
  maturityScore: number
  avgPct: number
  radarLabels: string[]
  radarValues: number[]
  hbarRows: MaturityHbarRow[]
  target?: number
}

const TARGET_DEFAULT = 3

function levelLabel(
  score: number,
  t: (k: string, o?: Record<string, unknown>) => unknown
) {
  const pick = (key: string, fallback: string) =>
    String(t(key, { defaultValue: fallback }) ?? fallback)

  if (score >= 4.5)
    return pick('analysis.maturityAnalysis.levels.optimizing', 'Optimizing')
  if (score >= 3.5)
    return pick('analysis.maturityAnalysis.levels.managed', 'Managed')
  if (score >= 2.5)
    return pick('analysis.maturityAnalysis.levels.defined', 'Defined')
  if (score >= 1.5)
    return pick('analysis.maturityAnalysis.levels.repeatable', 'Repeatable')
  return pick('analysis.maturityAnalysis.levels.initial', 'Initial')
}

function barColor(v: number) {
  if (v >= 4) return '#076B5C'
  if (v >= 3) return '#0D9B87'
  if (v >= 2) return '#C98A16'
  if (v > 0) return '#D14A3E'
  return '#CDD8D6'
}

function valueTone(v: number): 'coral' | 'amber' | 'teal' | 'deep' | 'muted' {
  if (!v) return 'muted'
  if (v >= 4) return 'deep'
  if (v >= 3) return 'teal'
  if (v >= 2) return 'amber'
  return 'coral'
}

function pillClass(v: number) {
  const tone = valueTone(v)
  return `ma-pdf-pill ma-pdf-pill--${tone}`
}

const IndicatorMaturiteConformitePdfTemplate = React.forwardRef<
  HTMLDivElement,
  MaturityPdfProps
>(function IndicatorMaturiteConformitePdfTemplate(
  {
    computedOn,
    maturityScore,
    avgPct,
    radarLabels,
    radarValues,
    hbarRows,
    target = TARGET_DEFAULT,
  },
  ref
) {
  const { t } = useTranslation()
  const maturityPct = Math.min(100, Math.max(0, (maturityScore / 5) * 100))
  const maturityTone = valueTone(maturityScore)
  const avgTone = avgPct > 0 ? valueTone(avgPct / 20) : 'muted'
  const maturityLevel = Math.max(1, Math.min(5, Math.round(maturityScore) || 1))

  const stats = useMemo(() => {
    if (!hbarRows.length) {
      return { avg: 0, above: 0, below: 0, total: 0 }
    }
    const sum = hbarRows.reduce((acc, r) => acc + r.v, 0)
    const above = hbarRows.filter((r) => r.v >= target).length
    return {
      avg: sum / hbarRows.length,
      above,
      below: hbarRows.length - above,
      total: hbarRows.length,
    }
  }, [hbarRows, target])

  const scaleItems = [
    { color: '#D14A3E', label: t('analysis.maturityAnalysis.levels.initial') },
    {
      color: '#C98A16',
      label: t('analysis.maturityAnalysis.levels.repeatable'),
    },
    { color: '#0D9B87', label: t('analysis.maturityAnalysis.levels.defined') },
    { color: '#076B5C', label: t('analysis.maturityAnalysis.levels.managed') },
  ]

  return (
    <div ref={ref} className="ma-pdf" data-pdf-ready="true">
      <header className="ma-pdf-hero avoid-break">
        <div className="ma-pdf-hero-top">
          <div className="ma-pdf-brand">
            <span className="ma-pdf-brand-mark">
              <img src={brandLogo} alt="" />
            </span>
            <div>
              <span className="ma-pdf-brand-name">INCYIA</span>
              <span className="ma-pdf-brand-tag">
                {t('analysis.maturityAnalysis.pdfDocType')}
              </span>
            </div>
          </div>
          <div className="ma-pdf-date-chip">
            <span className="ma-pdf-date-label">
              {t('analysis.maturityAnalysis.pdfReportDate')}
            </span>
            <strong>{computedOn}</strong>
          </div>
        </div>
        <p className="ma-pdf-eyebrow">
          {t('analysis.maturityAnalysis.eyebrow')}
        </p>
        <h1 className="ma-pdf-title">
          {t('analysis.maturityAnalysis.pageTitle')}
        </h1>
        <p className="ma-pdf-sub">
          {t('analysis.maturityAnalysis.subtitle', { date: computedOn })}
        </p>
        <div className="ma-pdf-scale">
          {scaleItems.map((item) => (
            <span key={item.label} className="ma-pdf-scale-item">
              <i style={{ background: item.color }} />
              {item.label}
            </span>
          ))}
        </div>
      </header>

      <p className="ma-pdf-section-label avoid-break">
        {t('analysis.maturityAnalysis.pdfSectionKpis')}
      </p>
      <section className="ma-pdf-kpi-grid avoid-break pdf-page-group">
        <div className={`ma-pdf-kpi ma-pdf-kpi--${maturityTone}`}>
          <div className="ma-pdf-kpi-head">
            <p className="ma-pdf-kpi-label">
              {t('analysis.maturityAnalysis.globalMaturityRateISO')}
            </p>
            <span className={pillClass(maturityScore)}>
              {t('analysis.maturityAnalysis.levelBadgeShort', {
                level: maturityLevel,
                label: levelLabel(maturityScore, t),
              })}
            </span>
          </div>
          <div className={`ma-pdf-kpi-value is-${maturityTone}`}>
            {`${formatDecimal(maturityScore)} / 5`}
          </div>
          <div className="ma-pdf-bar">
            <span
              style={{
                width: `${maturityPct}%`,
                background: barColor(maturityScore),
              }}
            />
          </div>
          <p className="ma-pdf-kpi-desc">
            {t('analysis.maturityAnalysis.maturityKpiDesc', {
              level: maturityLevel,
              label: levelLabel(maturityScore, t),
              target: formatDecimal(target),
            })}
          </p>
        </div>

        <div className={`ma-pdf-kpi ma-pdf-kpi--${avgTone}`}>
          <div className="ma-pdf-kpi-head">
            <p className="ma-pdf-kpi-label">
              {t('analysis.maturityAnalysis.globalAverageActionsPerControl')}
            </p>
            <span className={pillClass(avgPct / 20)}>
              {avgPct > 0
                ? t('analysis.maturityAnalysis.pdfCoverageOn')
                : t('analysis.maturityAnalysis.pdfCoverageOff')}
            </span>
          </div>
          <div className={`ma-pdf-kpi-value is-${avgTone}`}>
            {`${formatDecimal(avgPct)} %`}
          </div>
          <div className="ma-pdf-bar">
            <span
              style={{
                width: `${avgPct}%`,
                background: avgPct > 0 ? '#0D9B87' : '#CDD8D6',
              }}
            />
          </div>
          <p className="ma-pdf-kpi-desc">
            {avgPct > 0
              ? t('analysis.maturityAnalysis.actionsKpiDesc')
              : t('analysis.maturityAnalysis.actionsKpiEmpty')}
          </p>
        </div>
      </section>

      <p className="ma-pdf-section-label avoid-break">
        {t('analysis.maturityAnalysis.pdfSectionRadar')}
      </p>
      <section className="ma-pdf-card ma-pdf-card--radar avoid-break pdf-page-group">
        <div className="ma-pdf-card-head ma-pdf-card-head--teal">
          <div>
            <h2>{t('analysis.maturityAnalysis.securityCheckFamilyScores')}</h2>
            <p className="ma-pdf-card-sub">
              {t('analysis.maturityAnalysis.pdfRadarHint', {
                target: formatDecimal(target),
              })}
            </p>
          </div>
          <span className="ma-pdf-legend">
            <span className="ma-pdf-swatch" />
            {t('analysis.maturityAnalysis.scfScores')}
            <span className="ma-pdf-dash" />
            {t('analysis.maturityAnalysis.targetLegend', {
              target: formatDecimal(target),
            })}
          </span>
        </div>
        <div className="ma-pdf-card-body ma-pdf-radar">
          {radarLabels.length > 0 ? (
            <MaturityRadar
              labels={radarLabels}
              values={radarValues}
              max={5}
              target={target}
            />
          ) : (
            <p className="ma-pdf-empty">
              {t('analysis.maturityAnalysis.noData')}
            </p>
          )}
        </div>
      </section>

      <p className="ma-pdf-section-label">
        {t('analysis.maturityAnalysis.pdfSectionBars')}
      </p>

      {stats.total > 0 ? (
        <div className="ma-pdf-summary avoid-break">
          <div className="ma-pdf-summary-item">
            <span>{t('analysis.maturityAnalysis.pdfStatFamilies')}</span>
            <strong>{stats.total}</strong>
          </div>
          <div className="ma-pdf-summary-item">
            <span>{t('analysis.maturityAnalysis.pdfStatAverage')}</span>
            <strong className={`is-${valueTone(stats.avg)}`}>
              {formatDecimal(stats.avg)}
            </strong>
          </div>
          <div className="ma-pdf-summary-item is-good">
            <span>
              {t('analysis.maturityAnalysis.pdfStatAbove', {
                target: formatDecimal(target),
              })}
            </span>
            <strong>{stats.above}</strong>
          </div>
          <div className="ma-pdf-summary-item is-warn">
            <span>
              {t('analysis.maturityAnalysis.pdfStatBelow', {
                target: formatDecimal(target),
              })}
            </span>
            <strong>{stats.below}</strong>
          </div>
        </div>
      ) : null}

      <section className="ma-pdf-card pdf-page-group">
        <div className="ma-pdf-card-head ma-pdf-card-head--navy">
          <div>
            <h2>{t('analysis.maturityAnalysis.maturityRates')}</h2>
            <p className="ma-pdf-card-sub">
              {t('analysis.maturityAnalysis.hbarHint', {
                count: hbarRows.length,
              })}
            </p>
          </div>
        </div>
        <div className="ma-pdf-card-body">
          {hbarRows.length > 0 ? (
            <div className="ma-pdf-hbars">
              {hbarRows.map((row, idx) => {
                const pct = Math.min(100, Math.max(0, (row.v / 5) * 100))
                const tone = valueTone(row.v)
                return (
                  <div
                    className={`ma-pdf-hbar ma-pdf-hbar--${tone}`}
                    key={`${row.name}-${idx}`}
                  >
                    <div className="ma-pdf-hbar-rank">{idx + 1}</div>
                    <div className="ma-pdf-hbar-label">
                      {row.code ? (
                        <span className="ma-pdf-code">{row.code}</span>
                      ) : null}
                      {row.name}
                    </div>
                    <div className="ma-pdf-bar ma-pdf-bar--row">
                      <span
                        style={{
                          width: `${pct}%`,
                          background: barColor(row.v),
                        }}
                      />
                    </div>
                    <div className={`ma-pdf-hbar-val is-${tone}`}>
                      {`${formatDecimal(row.v)} / 5`}
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <p className="ma-pdf-empty">
              {t('analysis.maturityAnalysis.noData')}
            </p>
          )}
        </div>
      </section>

      <footer className="ma-pdf-footer">
        <div className="ma-pdf-footer-brand">
          <span className="ma-pdf-footer-dot" />
          {t('analysis.maturityAnalysis.pdfFooterBrand')}
        </div>
        <span>
          {t('analysis.maturityAnalysis.pdfGenerated', {
            date: computedOn,
          })}
        </span>
      </footer>
    </div>
  )
})

export default IndicatorMaturiteConformitePdfTemplate
